/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { BuilderUserContext } from '@gh/builder-shared';
import {
  IS_REGION_AU_OR_NZ,
  IS_REGION_US,
  NumberTypes,
  queryFormEditable,
  queryFormMode,
  State,
} from '@gh/common-shared';
import { isEditFormMode } from '@gh/core-ui';
import { inDefaultCurrency } from '@gh/core-util';
import { combineLatest$ } from '@gh/rx';
import { map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import { QuoteDetailsCommandService } from '../quote-details-command.service';
import {
  queryQuoteBottomInfo,
  queryQuoteDerivedInfo,
  queryQuoteIsApproved,
  queryQuoteIsProduced,
  queryQuoteIsProfitSupported,
  queryQuoteIsProfitVisible,
  QuoteDetailsState,
} from '../quote-details.state';

@Component({
  standalone: false,
  selector: 'gh-quote-details-bottom-panel',
  templateUrl: 'quote-details-bottom-panel.component.html',
  styleUrls: ['quote-details-bottom-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuoteDetailsBottomPanelComponent implements OnInit {
  memo$: Observable<string>;
  isMemoOnTop$: Observable<boolean>;
  rfqPrice1$: Observable<number>;
  rfqPrice2$: Observable<number>;
  profit$: Observable<number>;
  pbSalesPrice$: Observable<number>;
  pbCost$: Observable<number>;

  origSalesTax$: Observable<number>;
  adjSalesTax$: Observable<number>;
  totalSalesTax$: Observable<number>;

  canApprove$: Observable<boolean>;
  canUnapprove$: Observable<boolean>;
  supportsProfit$: Observable<boolean>;
  isProfitVisible$: Observable<boolean>;
  approvedByName$: Observable<Maybe<string>>;
  approvedDate$: Observable<Maybe<Date>>;

  calculationNumber$: Observable<number>;
  calculationStr$: Observable<string>;

  profitOppositeLabel$: Observable<string>;

  editMode$: Observable<boolean>;
  editable$: Observable<boolean>;

  usRegion = IS_REGION_US;
  auOrNzRegion = IS_REGION_AU_OR_NZ;

  constructor(private userContext: BuilderUserContext,
              private state: State<QuoteDetailsState>,
              private commands: QuoteDetailsCommandService) {

  }

  ngOnInit(): void {
    const bottom$ = this.state.select(queryQuoteBottomInfo);
    const derived$ = this.state.select(queryQuoteDerivedInfo);

    this.editMode$ = this.state.select(queryFormMode).pipe(map$(isEditFormMode));
    this.editable$ = this.state.select(queryFormEditable);

    this.memo$ = bottom$.pipe(map$((bottom) => bottom.memo));
    this.isMemoOnTop$ = bottom$.pipe(map$((bottom) => bottom.isMemoOnTop));
    this.rfqPrice1$ = derived$.pipe(map$((derived) => derived.rfqPrice1));
    this.rfqPrice2$ = derived$.pipe(map$((derived) => derived.rfqPrice2));
    this.profit$ = derived$.pipe(map$((derived) => derived.profit));
    this.pbSalesPrice$ = derived$.pipe(map$((derived) => derived.pbSalesPrice));
    this.pbCost$ = derived$.pipe(map$((derived) => derived.pbCost));
    this.origSalesTax$ = derived$.pipe(map$((derived) => derived.origSalesTax));
    this.adjSalesTax$ = derived$.pipe(map$((derived) => derived.adjSalesTax));
    this.totalSalesTax$ = derived$.pipe(map$((derived) => derived.totalSalesTax));
    this.approvedByName$ = derived$.pipe(map$((derived) => derived.approvedByName));
    this.approvedDate$ = derived$.pipe(map$((derived) => derived.approvedDate));
    this.calculationNumber$ = derived$.pipe(map$(({ pbPrice, pbCost, profit }) => pbPrice - pbCost + profit));
    this.calculationStr$ = derived$.pipe(map$(({ pbPrice, pbCost, profit }) => profit >= 0 ?
      `${inDefaultCurrency(NumberTypes.Price.format(pbPrice))} - ${NumberTypes.Price.format(pbCost)} + ${NumberTypes.Price.format(
        profit)}` :
      `${inDefaultCurrency(NumberTypes.Price.format(pbPrice))} - ${NumberTypes.Price.format(pbCost)} - ${NumberTypes.Price.format(
        Math.abs(profit))}`));

    const producedAndApproved$ = combineLatest$(
      this.state.select(queryQuoteIsProduced),
      this.state.select(queryQuoteIsApproved));

    this.canApprove$ = producedAndApproved$.pipe(map$(([produced, approved]) => produced && !approved));
    this.canUnapprove$ = producedAndApproved$.pipe(map$(([produced, approved]) => produced && approved));

    this.supportsProfit$ = this.state.select(queryQuoteIsProfitSupported);
    this.isProfitVisible$ = this.state.select(queryQuoteIsProfitVisible);

    this.profitOppositeLabel$ = this.isProfitVisible$.pipe(map$((isVisible) =>
      isVisible ? 'lbl_hide_profit' : 'lbl_show_profit'));
  }

  onMemoChange(memo: string): void {
    this.commands.setMemo(memo);
  }

  onMemoOnTopChange(isMemoOnTop: boolean): void {
    this.commands.setMemoOnTop(isMemoOnTop);
  }

  onOpenStructure(): void {
    this.commands.openStructure();
  }

  onApprove(): void {
    this.commands.approve();
  }

  onUnapprove(): void {
    this.commands.unapprove();
  }

  onToggleProfitVisiblity(): void {
    this.commands.toggleProfitVisiblity(!this.state.project(queryQuoteIsProfitVisible));
  }
}
